import { Component, Structure } from '@wix/thunderbolt-becky-types'
import { PINNED_LAYER_SUFFIX, RESPONSIVE_PAGE_CONTAINER_PREFIX, SECTIONS_CONTAINER_ID } from '../constants'

const isFixed = (compLayout: Component['layout']) => compLayout && compLayout.fixedPosition

const getChildrenGroupsWithPinnedLayer = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Component['layout'] },
	comp: Component
): Structure => {
	const compId = comp.id

	const pinnedChildren: Array<string> = []
	const nonPinnedChildren: Array<string> = []
	childrenIds.forEach((childId) => {
		if (isFixed(childrenLayout[childId])) {
			pinnedChildren.push(childId)
		} else {
			nonPinnedChildren.push(childId)
		}
	})

	const shouldUsePageMeshOverrides = comp.componentType === 'Page'
	const shouldUseHeaderMeshOverrides = comp.componentType === 'HeaderContainer'
	const compStructure = { [compId]: comp }

	const containerId = `Container${compId}`
	compStructure[containerId] = {
		id: containerId,
		components: nonPinnedChildren,
		componentType: 'Group',
		skin: comp.skin,
		layout: { height: comp.layout.height, x: comp.layout.x, y: comp.layout.y },
		type: 'Container',
		parent: compId,
		styleId: '',
		metaData: {
			shouldUsePageMeshOverrides,
			shouldUseHeaderMeshOverrides,
		},
		propertyQuery: comp.propertyQuery,
		dataQuery: comp.dataQuery,
	} as Component
	comp.components = [containerId]

	if (pinnedChildren.length) {
		const pinnedLayerId = `PinnedLayer${compId}`
		const pinnedLayer = {
			id: pinnedLayerId,
			components: pinnedChildren,
			componentType: 'PinnedLayer',
			layout: {},
			type: 'Container',
			parent: compId,
			styleId: '',
			metaData: {},
		} as Component

		comp.components.push(pinnedLayerId)
		compStructure[pinnedLayerId] = pinnedLayer
	}

	return compStructure
}

const getResponsiveChildrenGroups = (
	comp: Component,
	childrenIds: Array<string>,
	childrenData: { [compId: string]: { layout: Component['layout']; isFixed: boolean; compType: string } }
): Structure => {
	const compId = comp.id
	const compComponents = []
	const compStructure = { [compId]: comp }

	if (childrenIds.length > 0) {
		const containerId = `${RESPONSIVE_PAGE_CONTAINER_PREFIX}${compId}`
		const containerComponents: Array<string> = []

		const createContainer = (
			id: string,
			parentId: string,
			componentType: string,
			childrenContainer: Array<string>,
			components: Array<string>
		) => {
			const compContainer = {
				id,
				components,
				componentType,
				layout: {},
				type: 'Container',
				parent: parentId,
				styleId: '',
				metaData: {},
			} as Component

			childrenContainer.push(id)
			compStructure[id] = compContainer
		}
		const createPinnedLayerContainer = (id: string, parentId: string, childrenContainer: Array<string>) =>
			createContainer(`${id}${PINNED_LAYER_SUFFIX}`, parentId, 'PinnedLayer', childrenContainer, [id])

		const headerSections = childrenIds.filter((id) => childrenData[id].compType === 'HeaderSection')
		const sections = childrenIds.filter((id) => childrenData[id].compType === 'Section')
		const footerSections = childrenIds.filter((id) => childrenData[id].compType === 'FooterSection')
		const others = childrenIds.filter(
			(id) => !headerSections.includes(id) && !sections.includes(id) && !footerSections.includes(id)
		)

		headerSections.forEach((id) => {
			childrenData[id].isFixed
				? createPinnedLayerContainer(id, containerId, containerComponents)
				: containerComponents.push(id)
		})

		if (sections.length > 0) {
			const pageSectionsChildren: Array<string> = []
			sections.forEach((id) => {
				childrenData[id].isFixed
					? createPinnedLayerContainer(id, 'PageSections', pageSectionsChildren)
					: pageSectionsChildren.push(id)
			})
			createContainer(
				SECTIONS_CONTAINER_ID,
				containerId,
				'PageSections',
				containerComponents,
				pageSectionsChildren
			)
		}

		footerSections.forEach((id) => {
			childrenData[id].isFixed
				? createPinnedLayerContainer(id, containerId, containerComponents)
				: containerComponents.push(id)
		})
		others.forEach((id) => {
			childrenData[id].isFixed
				? createPinnedLayerContainer(id, containerId, containerComponents)
				: containerComponents.push(id)
		})

		if (containerComponents.length > 0) {
			compStructure[containerId] = {
				id: containerId,
				components: containerComponents,
				componentType: 'ResponsiveContainer',
				layout: {},
				type: 'Container',
				parent: compId,
			} as Component
			compComponents.push(containerId)
		}
	}

	comp.components = compComponents
	return compStructure
}

export { getChildrenGroupsWithPinnedLayer, getResponsiveChildrenGroups }
